import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Header from "../components/Header";
import Footer from "../components/Footer";
import BookDemo from '../components/BookDemo';
import { Link } from 'react-router-dom';

export default function Features() {
    const [showBookDemo, setShowBookDemo] = useState(false);

    const comparisonData = [
        { cap: "Purchase & Vendor Management", tally: "Included", adeya: "Full Automation" },
        { cap: "Sales & Customer Records", tally: "Included", adeya: "Full Automation" },
        { cap: "Unlimited Data Records", tally: "Missing", adeya: "Unlimited" },
        { cap: "Multi-Warehouse Management", tally: "Missing", adeya: "Unlimited Locations" },
        { cap: "Real-time Stock Valuation", tally: "Limited", adeya: "Instant Live-sync" },
        { cap: "Batch & Expiry Tracking", tally: "Partial", adeya: "Smart Alerts" },
        { cap: "Role-based Access Control", tally: "Missing", adeya: "Granular Controls" },
    ];

    // Fixed 3% potential savings examples
    // const savingsData = [
    //     {
    //         amount: "₹10 Crore",
    //         savings: "₹30,00,000",
    //         description: "Potential annual savings"
    //     }
    // ];

    return (
        <>
            <div className="bg-white text-slate-900 overflow-x-hidden">

                <Header />

                {/* --- HERO SECTION --- */}
                <section className="relative pt-40 pb-24 px-6 bg-gradient-to-b from-slate-50 to-white border-b border-slate-100">

                    <div className="container mx-auto text-center max-w-4xl">

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >

                            <span className="inline-flex items-center px-4 py-2 mb-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold tracking-wider uppercase">
                                INVENTORY DECISION PLATFORM
                            </span>

                            <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6 text-slate-900">
                                Make Smarter{" "}
                                <span className="text-sky-500">
                                    Inventory Decisions.
                                </span>
                            </h1>

                            <p className="text-slate-600 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl mx-auto">
                                {/* Adeya brings your inventory, sales and purchase
                                data together to help your team understand
                                what's happening and make better inventory decisions. */}
                                Adeya helps businesses identify excess, slow-moving and inefficient
                                inventory so you can reduce inventory and free up working capital.
                            </p>

                            <div className="flex flex-col sm:flex-row justify-center gap-4 items-center">

                                <Link to="/contact" className="w-full sm:w-auto">
                                    <button className="w-full sm:w-auto px-10 py-4 bg-sky-500 text-white rounded-xl font-bold hover:bg-sky-600 transition-all shadow-lg shadow-sky-500/20 active:scale-95">
                                        Get Started
                                    </button>
                                </Link>

                                <button
                                    onClick={() => setShowBookDemo(true)}
                                    className="w-full sm:w-auto px-10 py-4 border border-slate-200 text-slate-700 rounded-xl font-bold hover:bg-sky-500 hover:text-white transition-all active:scale-95"
                                >
                                    Watch Video Demo
                                </button>

                            </div>

                        </motion.div>

                    </div>

                </section>


                {/* --- CORE MODULES GRID --- */}
                <section className="py-24 container mx-auto px-6">

                    <div className="text-center mb-16">

                        <span className="text-sky-500 font-bold text-sm uppercase tracking-widest">
                            PLATFORM CAPABILITIES
                        </span>

                        <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight mt-3">
                            Built Around Better Decisions
                        </h2>

                        <p className="text-slate-500 mt-4 text-lg max-w-2xl mx-auto">
                            Everything your team needs to understand inventory,
                            sales and purchasing performance.
                        </p>

                    </div>


                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">

                        <FeatureBox
                            title="Inventory Visibility"
                            desc="Get a clear view of stock levels, movement and inventory value across your business."
                            icon="📦"
                        />

                        <FeatureBox
                            title="Purchase Insights"
                            desc="Understand purchasing patterns, supplier activity and where your inventory investment is going."
                            icon="🛒"
                        />

                        <FeatureBox
                            title="Sales Insights"
                            desc="Identify selling patterns and understand which products are contributing to your business."
                            icon="📈"
                        />

                        <FeatureBox
                            title="Multi-Location Visibility"
                            desc="Bring inventory information from multiple locations together for better operational visibility."
                            icon="🏢"
                        />

                        <FeatureBox
                            title="Stock Monitoring"
                            desc="Identify low-stock, excess-stock and slow-moving inventory before it impacts your operations."
                            icon="⚡"
                        />

                        <FeatureBox
                            title="Decision Intelligence"
                            desc="Turn inventory, sales and purchase data into clear insights that support smarter business decisions."
                            icon="🧠"
                        />

                        <FeatureBox
                            title="Real-time Analytics"
                            desc="Monitor important inventory metrics through clear dashboards and real-time operational insights."
                            icon="📊"
                        />

                        <FeatureBox
                            title="Financial Insights"
                            desc="Understand how inventory decisions affect working capital, margins and overall business performance."
                            icon="💰"
                        />

                        <FeatureBox
                            title="Cloud & Mobile Ready"
                            desc="Access your business information securely from wherever your team needs it."
                            icon="📱"
                        />

                    </div>

                </section>


                {/* --- COMPARISON TABLE --- */}

                <section className="py-24 bg-slate-900 text-white">

                    <div className="container mx-auto px-6">

                        <div className="text-center mb-16">

                            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-4">
                                How Adeya Stands Out
                            </h2>

                            <p className="text-slate-400 text-lg max-w-xl mx-auto">
                                Move beyond basic inventory management with
                                better visibility and decision support.
                            </p>

                        </div>


                        <div className="max-w-4xl mx-auto border border-white/10 rounded-3xl overflow-hidden bg-white/5 backdrop-blur-sm shadow-2xl">

                            <div className="grid grid-cols-2 md:grid-cols-3 p-6 border-b border-white/10 font-bold uppercase text-xs tracking-widest text-slate-400 bg-white/[0.02]">

                                <div>
                                    Feature Capability
                                </div>

                                <div className="hidden md:block text-center">
                                    Basic Systems
                                </div>

                                <div className="text-right md:text-center text-sky-400">
                                    Adeya
                                </div>

                            </div>


                            {comparisonData.map((row, i) => (

                                <div
                                    key={i}
                                    className="grid grid-cols-2 md:grid-cols-3 p-6 border-b border-white/5 items-center hover:bg-white/[0.03] transition"
                                >

                                    <div className="font-semibold text-sm md:text-base text-slate-200">
                                        {row.cap}
                                    </div>

                                    <div className="hidden md:block text-center text-sm font-medium">

                                        <span
                                            className={`px-3 py-1 rounded-full text-xs font-semibold ${row.tally === 'Missing'
                                                ? 'bg-rose-500/10 text-rose-400'
                                                : 'bg-slate-800 text-slate-300'
                                                }`}
                                        >
                                            {row.tally}
                                        </span>

                                    </div>

                                    <div className="text-right md:text-center text-sm font-bold text-sky-400">
                                        {row.adeya}
                                    </div>

                                </div>

                            ))}

                        </div>

                    </div>

                </section>


                {/* --- FINAL CTA --- */}

                <section className="py-24 px-6 bg-slate-50">

                    <div className="container mx-auto max-w-5xl bg-gradient-to-br from-sky-500 to-blue-600 rounded-[2.5rem] p-10 md:p-16 flex flex-col md:flex-row items-center justify-between gap-10 shadow-xl shadow-sky-500/10">

                        <div className="md:w-2/3 text-center md:text-left">

                            <span className="text-blue-100 text-sm font-bold uppercase tracking-widest">
                                READY TO GET STARTED?
                            </span>

                            <h2 className="text-3xl md:text-5xl font-black text-white mb-4 mt-3">
                                Make Better Inventory Decisions.
                            </h2>

                            <p className="text-sky-100 text-base md:text-lg max-w-xl">
                                Bring your inventory, sales and purchase data
                                together and give your team the visibility
                                they need to make better decisions.
                            </p>

                        </div>

                        <div className="md:w-1/3 flex justify-center w-full">

                            <button
                                onClick={() => setShowBookDemo(true)}
                                className="w-full md:w-auto whitespace-nowrap bg-white text-slate-900 px-8 py-5 rounded-xl font-black text-lg hover:bg-slate-100 transition-all shadow-lg active:scale-95 transform"
                            >
                                Book a Live Demo
                            </button>

                        </div>

                    </div>

                </section>


                <Footer />

            </div>


            {showBookDemo && (
                <BookDemo onClose={() => setShowBookDemo(false)} />
            )}

        </>
    );
}


function FeatureBox({ title, desc, icon }) {

    return (

        <div className="p-8 md:p-10 rounded-3xl border border-slate-100 bg-white hover:border-sky-400 hover:shadow-2xl hover:shadow-sky-100/60 transition-all group duration-300">

            <div className="text-4xl mb-6 group-hover:scale-110 group-hover:rotate-3 transition-transform inline-block">
                {icon}
            </div>

            <h3 className="text-xl md:text-2xl font-bold mb-3 text-slate-800">
                {title}
            </h3>

            <p className="text-slate-500 text-sm md:text-base leading-relaxed">
                {desc}
            </p>

        </div>

    );
}